'use client';

import {
  Container,
  WorkgroupCardsGridSection,
  TagFilter
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import { useEffect, useState } from 'react';
import flush from 'just-flush';
import { researchGroupQuery } from './researchGroupQuery';
import {
  extractNameFromProfileLike,
  filterOptionsFromDocuments,
  findInKeyArray
} from '@/utils';
import entryAsCardProps from './entryAsCardProps';
import { useClientUrl } from '@/hooks';

type Props = {
  title?: string;
  subtitle?: string;
  enableTaxonomyFilter?: boolean;
  groups: (typeof researchGroupQuery)[1];
  defaultFilters?: string[];
};

export default function ResearchGroupListingSection({
  title,
  subtitle,
  enableTaxonomyFilter,
  groups,
  defaultFilters
}: Props) {
  const url = useClientUrl();
  const { t, lang } = useTranslation();
  const [selected, setSelected] = useState<string[]>(
    defaultFilters?.length ? defaultFilters : ['all']
  );

  const sortedGroups = groups.sort((a, b) => {
    const aSortKey = a.researchGroupLeader
      ? extractNameFromProfileLike(a.researchGroupLeader)
      : null;
    const bSortKey = b.researchGroupLeader
      ? extractNameFromProfileLike(b.researchGroupLeader)
      : null;
    if (aSortKey && bSortKey) {
      const comparison = aSortKey.localeCompare(bSortKey);
      return comparison;
    }
    return 0;
  });

  const filteredGroups = sortedGroups.filter((group) => {
    if (selected.length === 1 && selected[0] === 'all') return true;
    if (!group.taxonomies?.length) return false;
    return group.taxonomies.some(
      (tax) => tax._id && selected.includes(tax._id)
    );
  });

  const filters = filterOptionsFromDocuments(sortedGroups, (doc) =>
    flush(
      doc.taxonomies?.map((tax) => {
        if (tax.type !== TaxonomyType.DISEASE_AREA) return null;
        const name = tax.name
          ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
          : null;
        if (!tax._id || !tax.slug?.current || !name?.value) return null;
        return { label: name.value, value: tax._id, slug: tax.slug.current };
      }) ?? []
    )
  ).sort((a, b) => a.label.localeCompare(b.label));

  const handleSelectFilter = (taxId: string) => {
    if (taxId === 'all') {
      setSelected(['all']);
    } else {
      setSelected(selected.filter((v) => v !== 'all').concat([taxId]));
    }
  };

  const filtersParam = url?.searchParams.get('filters');
  useEffect(() => {
    const filtersParamOptions = flush(
      (filtersParam?.split(',') ?? []).map((value) =>
        filters.find((entry) => entry.value === value || entry.slug === value)
      )
    );
    if (filtersParamOptions.length) {
      setSelected(filtersParamOptions.map((opt) => opt.value));
    }
  }, [filtersParam]);

  return (
    <>
      {enableTaxonomyFilter && (
        <Container name={ResearchGroupListingSection.name}>
          <TagFilter
            value={selected}
            options={[{ label: t('global:all'), value: 'all' }, ...filters]}
            onChange={setSelected}
          />
        </Container>
      )}
      <Container name={ResearchGroupListingSection.name}>
        <WorkgroupCardsGridSection
          title={title}
          subtitle={subtitle}
          size={enableTaxonomyFilter ? 'default' : 'small'}
          cards={flush(
            filteredGroups.map((group) =>
              entryAsCardProps(group, lang, handleSelectFilter)
            )
          )}
        />
      </Container>
    </>
  );
}
